import { type ComponentProps, type MouseEvent, useState } from 'react'

import { type CardIdType } from '@/defs/card'
import { type InViewStatus } from '@/hooks/useInView'
import { cn } from '@/utils/commonUtil'

import { CardFace } from './CardFace/CardFace'
import { CardFacePrices } from './CardFace/CardFacePrices/CardFacePrices'
import styles from './CardFaces.module.scss'

type CardFaceProps = ComponentProps<typeof CardFace>

type FaceType = 'back' | 'front'

type Props = {
  animate?: boolean
  back?: CardFaceProps
  cardId?: CardIdType
  front: CardFaceProps
  href?: string
  inViewStatus?: InViewStatus
  onClick?: (e: MouseEvent<HTMLElement>) => void
  showPrices?: boolean
  variant?: 'standard' | 'dfc' | 'partner'
}

export const CardFaces = ({
  animate,
  back,
  cardId,
  front,
  href,
  inViewStatus = 'inView',
  onClick,
  showPrices = true,
  variant,
}: Props) => {
  const [raised, setRaised] = useState<FaceType>('front')

  const hasPrices = !!cardId && showPrices

  const onClickFace = (e: MouseEvent, face: FaceType) => {
    const isTouch = e.nativeEvent instanceof PointerEvent && e.nativeEvent.pointerType === 'touch'
    if (face === raised || !isTouch) return
    e.preventDefault()
    setRaised(face)
  }

  return (
    <div className={cn(styles.container, { animate, hasPrices, inViewStatus, styles, variant })}>
      <div className={styles.faces}>
        {back && (
          <div
            className={cn(styles.cardBack, { raised: raised === 'back', styles })}
            onClickCapture={(e) => onClickFace(e, 'back')}
          >
            <CardFace {...back} href={href} inViewStatus={inViewStatus} onClick={onClick} />
          </div>
        )}
        <div
          className={cn(styles.cardFront, { raised: raised === 'front', styles })}
          onClickCapture={(e) => onClickFace(e, 'front')}
        >
          <CardFace {...front} href={href} inViewStatus={inViewStatus} onClick={onClick} />
        </div>
      </div>
      {hasPrices && (
        <div className={styles.prices}>
          <CardFacePrices cardId={cardId} enabled={inViewStatus === 'inView'} />
        </div>
      )}
    </div>
  )
}
